import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
    json,
    useLocation,
    useNavigate,
    useSearchParams,
    useLoaderData,
} from '@remix-run/react';
import { redirect } from '@remix-run/node';
import type {
    ActionFunctionArgs,
    LoaderFunctionArgs,
} from '@remix-run/server-runtime';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { getSearchPaginatedResults } from '@modules-rocco/search/api/get-search-paginated-results.server';
import { mapURLToFilterStates } from '@modules-rocco/filters/data-mappers/url-to-filter-state';
import { parseURL } from '@modules-rocco/url/api/parse-url';
import { generateURL } from '@modules-rocco/url/api/generate-url';
import { getSearchAggregations } from '@modules-rocco/search/api/get-search-aggregations';
import { setFilterStatesAggregations } from '@modules-rocco/filters/api/create-filter-states';
import { getAllTileFromESSearchResult } from '@modules-rocco/search/data-mappers/tile-search-result';
import { getRecentlyViewedServer } from '@modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { mapRecentlyViewedToTileViewModel } from '@modules-rocco/recently-viewed/data-mappers/tile-generic';
import { collectFallibleResources } from '@modules-rocco/root/api/collect-fallible-resources.server';
import { mapURLToSortOption } from '@modules-rocco/filters/data-mappers/url-to-sort-option';
import {
    DEFAULT_PAGE_SIZE,
    SEARCH_CATEGORIES,
} from '@modules-rocco/search/config/search-service.config';
import { useSearchInput } from '@rocco/ui/search/hooks/use-search-input';
import { SearchBar } from '@rocco/ui/search/components/SearchBar';
import {
    formatNumberWithCommas,
    roundToNearest,
} from '@rocco/utils/format-number';
import { mapSearchIndexToFilterTabsViewModel } from '@modules-rocco/search/data-mappers/search-index-to-filter-tabs';
import { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import { getSearchSuggestions } from '@modules-rocco/search/api/get-search-suggestions.server';
import { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { useRecentlySearched } from '@modules-rocco/search/hook/use-recently-searched';
import type { SearchSuggestion } from '@rocco/ui/search/view-models/search-suggestion';
import { SearchIndexes } from '@rocco/types/enum/search-indexes';
import { getSearchResultGroups } from '@modules-rocco/search/api/get-search-result-groups.server';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { isCategorySuggestion } from '@rocco/ui/search/utils/type-guards';
import { ClientOnly } from 'remix-utils';
import { uiStateAtomSearchClicked } from '@rocco/ui/website-header/states/ui-states-search-clicked';
import { SearchIsland } from '@rocco/ui/floating-island/components/SearchIsland';
import { useSearchTracking } from '@ui-rocco/hooks/use-search-tracking';
import { useFloatingIslandTracking } from '~/ui-rocco/hooks/use-floating-island-tracking';
import type { DirectoryItemAdmin } from '~/modules/directory/type';
import { getDirectoryItemByIDs } from '~/modules/directory/api/get-directory-items-admin.server';
import { PermissionEnum } from '@archipro-website/top-navigation/src/lib/types';
import { getPermissions } from '~/modules/user/hook/use-permissions';
import type { GrantedPermissions } from '~/modules/user/type';
import { emptyPaginationState } from '~/modules-rocco/pagination/util/create-pagination-state.server';
import { useSearchQuery } from '@ui-rocco/hooks/use-search-query';
import {
    searchPageDataAtom,
    type SearchPageData,
} from '~/modules-rocco/search/states/search-page-data-atom';
import { SearchPageContentSectionsDesktop } from '~/modules-rocco/search/components/SearchPageContentSectionsDesktop';
import { SearchPageContentSectionsMobile } from '~/modules-rocco/search/components/SearchPageContentSectionsMobile';
import {
    getCanonicalHreflang,
    createMetaTitle,
    createOGTags,
    createMetaDescription,
    mergeMeta,
} from '~/modules/seo';
import { getFilterSettings } from '~/modules-rocco/search/util/get-filter-settings';
import {
    getCachedSearchResults,
    setCachedSearchResults,
} from '~/modules-rocco/search/utils/search-cache';
import { useUser } from '~/modules/user/hook/use-user';
import { usePrefetchSearch } from '~/modules-rocco/search/hook/use-prefetch-search';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
import { getStatsNumbers } from '~/modules/error/api/get-stats-numbers.server';

export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger, appDisplayMode } = context;
    const url = new URL(request.url);
    const urlInfo = parseURL(url.pathname + url.search);
    const searchParams = url.searchParams;
    const searchQuery = searchParams.get('search') ?? '';
    const refererHeader = request.headers.get('referer') || '';
    const host = request.headers.get('host') || '';
    let loadFromOtherAPRoute = false;
    try {
        const protocol = refererHeader ? new URL(refererHeader).protocol : '';
        const hostUrl = protocol + '//' + host;
        loadFromOtherAPRoute = !refererHeader?.includes(`${hostUrl}/search`);
    } catch (error) {
        logger.error('Error parsing referer header', {
            error,
        });
    }
    const isFirstLoading = request.headers.get('sec-fetch-site') === 'none';
    if (loadFromOtherAPRoute && !isFirstLoading) {
        return json({
            appDisplayMode: context.appDisplayMode,
            searchQuery: searchQuery ?? '',
            loadFromOtherAPRoute,
            isFirstLoading,
        });
    }

    const category = urlInfo.params.category?.value;
    const filterSettings = getFilterSettings(category);
    const urlFilterStates = mapURLToFilterStates(
        filterSettings?.config ?? null,
        urlInfo,
        { visibilities: ['InLibrary'] }
    );

    const sortOption = mapURLToSortOption(
        urlInfo,
        // TODO: passing in anything other than undefined when there's a search query will cause the search result to be non relevant at all, Need to look into this
        urlFilterStates.states.search?.query
            ? undefined
            : SearchSortOptions.Featured
    );

    const [
        searchQueryResponse,
        user,
        recentlyViewedContent,
        aggregations,
        suggestedResults,
        statsNumbers,
    ] = await collectFallibleResources(
        [
            getSearchPaginatedResults(
                context,
                request,
                urlFilterStates.states.search?.searchIndex,
                urlFilterStates,
                DEFAULT_PAGE_SIZE,
                sortOption
            ),
            context.getUser(),
            getRecentlyViewedServer({ type: 'All' }, context),
            getSearchAggregations(
                context,
                request,
                urlFilterStates.states.search?.searchIndex,
                urlFilterStates
            ),
            getSearchSuggestions(request, context, {
                search: searchQuery,
                pageSize: 15,
                searchType: urlFilterStates.states.search?.searchIndex,
            }),
            getStatsNumbers(context),
        ],
        logger
    );

    const searchResultGroups =
        appDisplayMode === 'desktop'
            ? []
            : await getSearchResultGroups(
                  context,
                  request,
                  [
                      SearchIndexes.Products,
                      SearchIndexes.Professionals,
                      SearchIndexes.Projects,
                      SearchIndexes.Articles,
                  ],
                  urlFilterStates,
                  DEFAULT_PAGE_SIZE,
                  sortOption,
                  5 // limit to 5 hits per group
              );

    const searchResultGroupsTiles =
        searchResultGroups.map((group) => {
            return {
                index: group.index,
                tiles: getAllTileFromESSearchResult(
                    group.searchResponse.searchResults,
                    user
                ),
            };
        }) ?? [];

    if (searchQueryResponse?.pagination.isPageOutOfRange) {
        searchParams.delete('page');
        return redirect(`${url.pathname}?${searchParams.toString()}`, 302);
    }

    // update filterStates with aggregations
    const filterStates = setFilterStatesAggregations({
        searchIndex: urlFilterStates.states.search?.searchIndex,
        aggregations: aggregations ?? {},
        defaultSort: sortOption,
        filterStates: urlFilterStates,
    });

    const totalCount = searchQueryResponse?.pagination?.totalCount ?? 0;
    const filterTabs = mapSearchIndexToFilterTabsViewModel(
        url,
        appDisplayMode === 'mobile' ? 'All' : undefined
    );

    const rootGrants: GrantedPermissions = {
        editProfile: user
            ? getPermissions(user, PermissionEnum.BmProfileEdit)
            : false,
        editAdvertAdmin: user
            ? getPermissions(user, PermissionEnum.BmAdvertAdminEdit)
            : false,
    };

    const results = searchQueryResponse
        ? getAllTileFromESSearchResult(searchQueryResponse.searchResults, user)
        : [];

    // fetch admin data for search results
    let tilesAdminData: DirectoryItemAdmin[] = [];
    if (rootGrants.editProfile) {
        tilesAdminData = await getDirectoryItemByIDs({
            ...context,
            IDs: results.map((result) => result.id),
        });
    }

    return json({
        filterStates,
        searchQuery,
        searchIndex: urlFilterStates.states.search?.searchIndex,
        pagination:
            searchQueryResponse?.pagination ??
            emptyPaginationState({ pageSize: DEFAULT_PAGE_SIZE }),
        results,
        totalCount,
        filterTabs,
        appDisplayMode,
        recentlyViewedItems:
            recentlyViewedContent?.RecentlyViewedAll?.map((item) =>
                mapRecentlyViewedToTileViewModel(item, user, appDisplayMode)
            ) ?? [],
        suggestedCategories: suggestedResults?.categories ?? [],
        searchResultGroups: searchResultGroupsTiles,
        tilesAdminData,
        loadFromOtherAPRoute,
        isFirstLoading,
        statsNumbers,
    });
};

export const meta = mergeMeta<typeof loader>(
    () => {
        // Initial static meta tags, if any, or just an empty array
        return [];
    },
    (props) => {
        const { data, matches, location } = props;
        const { rootData } = getRouteData(matches) ?? { rootData: {} };
        const stage = rootData?.env?.stage;

        // searchQuery can be taken from data (loader) or directly from location for more safety
        const searchParams = new URLSearchParams(location.search);
        const searchQueryFromURL = searchParams.get('search');
        const effectiveSearchQuery =
            data?.searchQuery ?? searchQueryFromURL ?? '';

        const title = createMetaTitle(props, [effectiveSearchQuery, 'Search']);
        const description = createMetaDescription([]);
        const ogTags = createOGTags({
            meta: props,
            title,
            description,
        });
        const canonicals = getCanonicalHreflang(props);
        return [
            { title },
            { name: 'description', content: description },
            ...ogTags,
            ...canonicals,
            ...(stage === 'Production'
                ? [{ name: 'robots', content: 'noindex' }]
                : []),
        ];
    }
);

export const action = async (args: ActionFunctionArgs) => {
    const formData = await args.request.formData();
    const searchString = (formData.get('searchString') as string) ?? '';
    const user = JSON.parse(formData.get('user') as string);

    // Reconstruct the effective URL based on the searchString provided by the client
    const currentRequestUrl = new URL(args.request.url);
    const effectiveUrl = new URL(
        currentRequestUrl.origin + currentRequestUrl.pathname + searchString
    );

    // Create a new Request object with the effectiveURL for consistent parameter parsing server-side
    const effectiveGetRequest = new Request(effectiveUrl.toString(), {
        method: 'GET',
        headers: args.request.headers,
    });

    const urlInfo = parseURL(effectiveUrl.pathname + effectiveUrl.search);
    const { logger, appDisplayMode } = args.context;
    const searchParams = effectiveUrl.searchParams;
    const searchQuery = searchParams.get('search') ?? '';

    const category = urlInfo.params.category?.value;
    const filterSettings = getFilterSettings(category);
    const urlFilterStates = mapURLToFilterStates(
        filterSettings?.config ?? null,
        urlInfo,
        { visibilities: ['InLibrary'] }
    );

    const sortOption = mapURLToSortOption(
        urlInfo,
        // TODO: passing in anything other than undefined when there's a search query will cause the search result to be non relevant at all, Need to look into this
        urlFilterStates.states.search?.query
            ? undefined
            : SearchSortOptions.Featured
    );

    const [
        searchQueryResponse,
        recentlyViewedContent,
        aggregations,
        suggestedResults,
        statsNumbers,
    ] = await collectFallibleResources(
        [
            getSearchPaginatedResults(
                args.context,
                effectiveGetRequest,
                urlFilterStates.states.search?.searchIndex,
                urlFilterStates,
                DEFAULT_PAGE_SIZE,
                sortOption
            ),
            getRecentlyViewedServer({ type: 'All' }, args.context),
            getSearchAggregations(
                args.context,
                effectiveGetRequest,
                urlFilterStates.states.search?.searchIndex,
                urlFilterStates
            ),
            getSearchSuggestions(effectiveGetRequest, args.context, {
                search: searchQuery,
                pageSize: 15,
                searchType: urlFilterStates.states.search?.searchIndex,
            }),
            getStatsNumbers(args.context),
        ],
        logger
    );

    const searchResultGroups =
        appDisplayMode === 'desktop'
            ? []
            : await getSearchResultGroups(
                  args.context,
                  effectiveGetRequest,
                  [
                      SearchIndexes.Products,
                      SearchIndexes.Professionals,
                      SearchIndexes.Projects,
                      SearchIndexes.Articles,
                  ],
                  urlFilterStates,
                  DEFAULT_PAGE_SIZE,
                  sortOption,
                  undefined
              );

    const searchResultGroupsTiles =
        searchResultGroups.map((group) => {
            return {
                index: group.index,
                tiles: getAllTileFromESSearchResult(
                    group.searchResponse.searchResults,
                    user
                ),
            };
        }) ?? [];

    // update filterStates with aggregations
    const filterStates = setFilterStatesAggregations({
        searchIndex: urlFilterStates.states.search?.searchIndex,
        aggregations: aggregations ?? {},
        defaultSort: sortOption,
        filterStates: urlFilterStates,
    });

    const totalCount = searchQueryResponse?.pagination?.totalCount ?? 0;
    const filterTabs = mapSearchIndexToFilterTabsViewModel(
        effectiveUrl,
        appDisplayMode === 'mobile' ? 'All' : undefined
    );

    const rootGrants: GrantedPermissions = {
        editProfile: user
            ? getPermissions(user, PermissionEnum.BmProfileEdit)
            : false,
        editAdvertAdmin: user
            ? getPermissions(user, PermissionEnum.BmAdvertAdminEdit)
            : false,
    };

    const results = searchQueryResponse
        ? getAllTileFromESSearchResult(searchQueryResponse?.searchResults, user)
        : [];

    // fetch admin data for search results
    let tilesAdminData: DirectoryItemAdmin[] = [];
    if (rootGrants.editProfile && appDisplayMode === 'desktop') {
        tilesAdminData = await getDirectoryItemByIDs({
            ...args.context,
            IDs: results.map((result) => result.id),
        });
    }

    return json({
        filterStates,
        searchQuery,
        searchIndex: urlFilterStates.states.search?.searchIndex,
        pagination:
            searchQueryResponse?.pagination ??
            emptyPaginationState({ pageSize: DEFAULT_PAGE_SIZE }),
        results,
        totalCount,
        filterTabs,
        appDisplayMode,
        recentlyViewedItems:
            recentlyViewedContent?.RecentlyViewedAll?.map((item) =>
                mapRecentlyViewedToTileViewModel(item, user, appDisplayMode)
            ) ?? [],
        suggestedCategories: suggestedResults?.categories ?? [],
        searchResultGroups: searchResultGroupsTiles,
        tilesAdminData,
        statsNumbers,
    });
};

type MobileSearchPageView = 'results' | 'recentlySearched' | 'suggestions';

export default function SearchPage() {
    const loaderData = useLoaderData<typeof loader>();
    const { appDisplayMode } = loaderData;
    const user = useUser();
    const location = useLocation();
    const setAtomPageData = useSetAtom(searchPageDataAtom);
    const pageData = useAtomValue(searchPageDataAtom);
    const [urlSearchParams] = useSearchParams();
    const queryDirectlyFromUrl = urlSearchParams.get('search') ?? '';
    const userString = JSON.stringify(user);
    const [userStringState, setUserStringState] = useState(userString);
    // Manages loading state based on the fetcher's activity.
    const [isLoading, setIsLoading] = useState(true);
    const prefetchSearch = usePrefetchSearch(setIsLoading);

    useEffect(() => {
        setUserStringState(userString);
    }, [userString]);

    useEffect(() => {
        return () => {
            setAtomPageData(null);
        };
    }, [setAtomPageData]);

    // Fetches search results when URL (search params or pathname) changes.
    useEffect(() => {
        const searchString = location.search;
        const currentPathname = location.pathname;
        const cacheKey = currentPathname + searchString;
        const cachedData = getCachedSearchResults(cacheKey);
        const searchParams = new URLSearchParams(location.search);
        const searchQuery = searchParams.get('search') ?? '';
        setIsLoading(true);
        if (cachedData) {
            setAtomPageData(cachedData);
            setIsLoading(false);
            return;
        } else {
            if (loaderData.loadFromOtherAPRoute && !loaderData.isFirstLoading) {
                prefetchSearch({ searchStringForAction: searchQuery });
                return;
            }
            const pageData = loaderData as unknown as SearchPageData;
            setAtomPageData(pageData);
            setIsLoading(false);
            if (!getCachedSearchResults(cacheKey)) {
                setCachedSearchResults(cacheKey, pageData);
            }
            return;
        }
    }, [
        location.search,
        location.pathname,
        setAtomPageData,
        userStringState,
        prefetchSearch,
        loaderData.loadFromOtherAPRoute,
        loaderData,
    ]);

    const searchIndex = pageData?.searchIndex;
    const filterStates = pageData?.filterStates;

    const statsNumbers = pageData?.statsNumbers ?? null;
    const trackingConfig = useSearchTracking({ isSearchPage: true });
    const floatingIslandTrackingConfig = useFloatingIslandTracking();

    const memoizedEmptyFiltersConfig = useMemo(
        () => ({
            resultName: '',
            barFilters: [],
            modalFilters: [],
        }),
        []
    );
    const memoizedEmptyFilterStates = useMemo(() => ({ states: {} }), []);

    const filterSettings = getFilterSettings(searchIndex);
    const filterSortProps = useCategoryFilterSort(
        filterSettings ? filterSettings.config : memoizedEmptyFiltersConfig,
        filterSettings?.index,
        filterStates ?? memoizedEmptyFilterStates
    );

    const { addRecentSearch, recentlySearched, clearHistory } =
        useRecentlySearched();

    const queryForSearchHook = useSearchQuery(queryDirectlyFromUrl);

    const searchInput = useSearchInput({
        query: queryForSearchHook,
        searchIndex: searchIndex,
        doSearch: (finalQuery, searchIdx) => {
            const category = SEARCH_CATEGORIES.find(
                (c) => c.index === searchIdx
            )?.category;

            if (finalQuery) {
                setSearchParams({ search: finalQuery });
                addRecentSearch(finalQuery);
                navigate(
                    generateURL('/search', {
                        search: finalQuery,
                        ...(category ? { category } : {}),
                    })
                );
            } else {
                setSearchParams({});
                navigate('/search');
            }
        },
    });

    const {
        searchQuery: inputSearchQuery,
        handleInputChange,
        handleKeyPress,
        handleSearch,
        suggestionGroups,
        clearSearchQuery,
    } = searchInput;

    const hasSuggestions = suggestionGroups.some(
        (group) => group.options.length > 0
    );

    const [view, setView] = useState<MobileSearchPageView>(
        queryDirectlyFromUrl ? 'results' : 'recentlySearched'
    );

    // Determines the mobile view (results, suggestions, or recently searched) based on query and suggestion state.
    useEffect(() => {
        const showSuggestions =
            inputSearchQuery !== queryDirectlyFromUrl && hasSuggestions;
        const showRecentSearches =
            !queryDirectlyFromUrl && !inputSearchQuery && !hasSuggestions;

        setView(
            showSuggestions
                ? 'suggestions'
                : showRecentSearches
                  ? 'recentlySearched'
                  : 'results'
        );
    }, [queryDirectlyFromUrl, inputSearchQuery, hasSuggestions]);

    const isMobile = appDisplayMode === 'mobile';

    const [, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [searchIconClicked, setSearchIconClicked] = useAtom(
        uiStateAtomSearchClicked
    );

    const searchSectionInputRef = useRef<HTMLInputElement>(null);
    // Focuses the search input when the header search icon is clicked.
    useEffect(() => {
        if (searchIconClicked) {
            searchSectionInputRef.current?.focus();
            setSearchIconClicked(false);
        }
    }, [searchIconClicked, setSearchIconClicked]);

    const placeholderTexts = [
        `Search ${formatNumberWithCommas(roundToNearest(statsNumbers?.ProductCount ?? 0))}+ products`,
        `Search ${formatNumberWithCommas(roundToNearest(statsNumbers?.ProfessionalCount ?? 0))}+ professionals`,
        `Search ${formatNumberWithCommas(roundToNearest(statsNumbers?.ProjectCount ?? 0))}+ projects`,
    ];

    const mobileProps = {
        autoFocus: true,
        hideSubmitButton: true,
        disableDropdown: true,
    };

    return (
        <div className="md:min-h-auto min-h-screen pb-20 md:pt-22">
            <div className="space-y-8 md:space-y-0">
                <div className="container mx-auto px-6 pt-3 md:max-w-[52.5rem] md:px-0 md:pb-22 md:pt-[6.375rem]">
                    <div>
                        <div className="flex w-full">
                            <SearchBar
                                key="searchpage-searchBar"
                                ref={searchSectionInputRef}
                                placeholderTexts={placeholderTexts}
                                value={inputSearchQuery}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyPress}
                                onSearchSubmit={(value) => {
                                    if (typeof value === 'string') {
                                        handleSearch(value);
                                        setIsLoading(true);
                                    }
                                }}
                                suggestionGroups={suggestionGroups}
                                onSearchSuggestionClick={(suggestion) => {
                                    if (isCategorySuggestion(suggestion)) {
                                        trackingConfig.onSearchSuggestionClick(
                                            suggestion.title
                                        );
                                        addRecentSearch(suggestion.title);
                                        navigate(suggestion.link);
                                    } else {
                                        trackingConfig.onSearchSuggestionClick(
                                            suggestion
                                        );
                                        handleSearch(suggestion);
                                        addRecentSearch(suggestion);
                                    }
                                }}
                                clearSearchQuery={() => {
                                    searchSectionInputRef.current?.focus();
                                    clearSearchQuery();
                                }}
                                displayMode={appDisplayMode as DisplayMode}
                                variant={
                                    isMobile ? 'searchPageMobile' : 'default'
                                }
                                recentlySearched={recentlySearched}
                                clearHistory={clearHistory}
                                onRecentlySearchedClick={(search) => {
                                    trackingConfig.onRecentSearchClick(search);
                                    setSearchParams({ search });
                                    addRecentSearch(search);
                                    navigate(
                                        generateURL('/search', {
                                            search,
                                        })
                                    );
                                }}
                                {...(isMobile ? mobileProps : {})}
                            />
                        </div>
                    </div>
                </div>
                <ClientOnly>
                    {() => (
                        <div>
                            {isMobile ? (
                                <SearchPageContentSectionsMobile
                                    view={view}
                                    filterSortProps={filterSortProps}
                                    suggestionGroups={suggestionGroups}
                                    inputSearchQuery={inputSearchQuery}
                                    isLoading={isLoading}
                                    fetcherState={
                                        isLoading ? 'loading' : 'idle'
                                    }
                                />
                            ) : (
                                <SearchPageContentSectionsDesktop
                                    filterSortProps={filterSortProps}
                                    onSuggestedCategoryClick={(
                                        category: SearchSuggestion
                                    ) => {
                                        trackingConfig.onSuggestedCategoryClick(
                                            category.title,
                                            category.link
                                        );
                                    }}
                                    isLoading={isLoading}
                                    fetcherState={
                                        isLoading ? 'loading' : 'idle'
                                    }
                                />
                            )}
                        </div>
                    )}
                </ClientOnly>
            </div>
            <ClientOnly fallback={<></>}>
                {() => (
                    <SearchIsland
                        input={searchInput}
                        placeholderTexts={placeholderTexts}
                        displayMode={appDisplayMode as DisplayMode}
                        scrollThreshold={isMobile ? 100 : 200}
                        onBeforeSearchSubmit={(query) => {
                            floatingIslandTrackingConfig.onPerformSearch(query);
                        }}
                    />
                )}
            </ClientOnly>
        </div>
    );
}
